<template>
  <div class="member-tags">
    <div
      v-for="(list, index) in displayMemberTags"
      :key="index"
      class="member-tags__row"
    >
      <div v-for="tag in list" :key="tag.id">
        <!-- 更多標籤 -->
        <MoreTag
          v-if="tag.id === 'more'"
          :showMoreTag="() => (showMoreTag = true)"
        />
        <!-- 新增標籤 -->
        <el-tag
          v-else-if="tag.id === 'add'"
          type="info"
          class="tag__add"
          disable-transitions
          size="small"
          @click="showSelector.tag = true"
        >
          {{ $t('member.memberInfoBlock.addTag') }}
        </el-tag>
        <!-- 基本標籤 -->
        <BaseTag v-else :tag="tag" :close="onRemoveTag" :closable="canEditTags" />
      </div>
    </div>

    <el-dialog
      :title="$t('member.memberInfoBlock.memberTags')"
      :visible="showMoreTag"
      width="600px"
      :close-on-click-modal="true"
      @close="showMoreTag = false"
    >
      <div class="flex gap-2 flex-wrap">
        <div v-for="(tag, index) in currentMemberTags" :key="index">
          <!-- 基本標籤 -->
          <BaseTag :tag="tag" :close="onRemoveTag" :closable="canEditTags" />
        </div>
        <el-tag
          v-if="canEditTags"
          type="info"
          class="tag__add"
          disable-transitions
          size="small"
          @click="showSelector.tag = true"
        >
          {{ $t('member.memberInfoBlock.addTag') }}
        </el-tag>
      </div>
    </el-dialog>

    <MemberTagAdvanceSelect
      :show.sync="showSelector.tag"
      :options="manualTagOptions"
      :modelValue="memberCustomTagIds"
      @update:modelValue="updateCustomTags"
      @close="showSelector.tag = false"
    />
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref } from 'vue'
import { chunk } from 'lodash'
import { t } from '@/plugins/i18n/i18n'
import BaseTag from './BaseTag.vue'
import MoreTag from './MoreTag.vue'
import MemberTagAdvanceSelect from '@/components/Select/MemberTagAdvanceSelect.vue'
import { usePermissions } from '@/use/permissions'

export default defineComponent({
  name: 'MemberTags',
  components: {
    BaseTag,
    MoreTag,
    MemberTagAdvanceSelect,
  },
  props: {
    member: { type: Object, default: () => ({}) },
    mTagsList: { type: Array, default: () => [] },
    customTagsList: { type: Array, default: () => [] },
  },
  emits: ['update:customTags'],
  setup (props, { emit }) {
    const { checkAction } = usePermissions()
    // 店家方案沒有會員等級（GIMMATEK 不提供）時，不顯示等級系統標籤（CNCY-10766）
    const showMemberLevel = computed(() => checkAction('admin.memberLevelConfig.page'))
    // 沒有編輯會員權限（例如服務人員）只能查看標籤
    const canEditTags = computed(() => checkAction('admin.member.update'))
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200)
    const onResize = () => { windowWidth.value = window.innerWidth }
    onMounted(() => window.addEventListener('resize', onResize))
    onUnmounted(() => window.removeEventListener('resize', onResize))

    const showMoreTag = ref(false)
    const showSelector = reactive({
      tag: false,
    })

    // 依規則建立的自動標籤（isAuto）不提供手動貼標，僅保留非自動標籤供選擇
    const manualTagOptions = computed(() =>
      props.customTagsList.filter((t) => !t.isAuto),
    )

    const memberCustomTags = computed(() => {
      const tagsSource = props.member?.MTags || []
      return props.customTagsList.filter((i) =>
        tagsSource.some((j) => j.id === i.id),
      )
    })

    const memberCustomTagIds = computed(() =>
      memberCustomTags.value.map((t) => t.id),
    )

    const memberSystemTags = computed(() => {
      const tagsSource = props.member?.SystemMTag || []
      return props.mTagsList.filter((i) =>
        tagsSource.some((j) => j.id === i.id) &&
        (showMemberLevel.value || i.systemGroup !== 'memberLevel'),
      )
    })

    const currentMemberTags = computed(() =>
      memberSystemTags.value.concat(memberCustomTags.value),
    )

    const totalDisplayTagCount = computed(() => {
      return windowWidth.value > 1200 ? 5 : 3
    })

    const displayMemberTags = computed(() => {
      const mixedTags = currentMemberTags.value.slice(
        0,
        totalDisplayTagCount.value,
      )
      if (currentMemberTags.value.length > totalDisplayTagCount.value) {
        mixedTags.push({ id: 'more', name: t('member.memberInfoBlock.moreTagLabel') })
      }
      if (canEditTags.value) mixedTags.push({ id: 'add', name: t('common.add.text') })
      return chunk(mixedTags, 10)
    })

    const updateCustomTags = (ids) => {
      emit('update:customTags', ids)
    }

    const onRemoveTag = (tagId) => {
      const mTags = props.member?.MTags || []
      emit(
        'update:customTags',
        mTags.filter((i) => i.id !== tagId).map((i) => i.id),
      )
    }

    return {
      canEditTags,
      currentMemberTags,
      displayMemberTags,
      manualTagOptions,
      onRemoveTag,
      updateCustomTags,
      memberCustomTags,
      memberCustomTagIds,
      showMoreTag,
      showSelector,
    }
  },
})
</script>

<style lang="scss" scoped>
.member-tags {
  flex-grow: 1;
  @apply flex flex-col gap-y-[8px];
}

.member-tags__row {
  @apply flex flex-row flex-nowrap gap-[8px] h-[25px];
}

.tag__add {
  @apply flex cursor-pointer bg-white;
}
</style>
